import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  MenuItem,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import { XeroContactsMissingPage } from './XeroContactsMissingPage';
import { XeroInvoicesMissingPage } from './XeroInvoicesMissingPage';
import { XeroOutstandingPage } from './XeroOutstandingPage';
import {
  getXeroMetrics,
  getXeroWebhookHealth,
  runBackfillXeroContactIds,
  runBackfillXeroContactIdsByName,
  runBackfillXeroInvoiceIds,
  runBackfillXeroInvoiceResourceTypes,
  runMarkZeroInvoices,
  runRefreshXeroModified,
  runSendMonthlyXeroInvoices,
} from '../services/xeroIntegrationService';
import type {
  SendMonthlyXeroInvoicesResponse,
  WebhookHealth,
  XeroMetrics,
} from '../services/xeroIntegrationService';

type SubView = 'contacts' | 'invoices' | 'outstanding' | null;
type TabView = 'ops' | 'webhook';

const LIMIT_DEFAULT = 100;
const LIMIT_MAX = 500;
const LIMIT_HELPER = `Limit caps rows processed per call (max ${LIMIT_MAX}) to stay under the 230s function timeout.`;

export const XeroPage: React.FC = () => {
  const [subView, setSubView] = useState<SubView>(null);
  const [tabView, setTabView] = useState<TabView>('ops');

  // Metrics
  const [metrics, setMetrics] = useState<XeroMetrics | null>(null);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const metricsAbortRef = useRef<AbortController | null>(null);

  const loadMetrics = useCallback(async () => {
    metricsAbortRef.current?.abort();
    const controller = new AbortController();
    metricsAbortRef.current = controller;
    setMetricsLoading(true);
    setMetricsError(null);
    try {
      const data = await getXeroMetrics(undefined, controller.signal);
      setMetrics(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setMetricsError(err instanceof Error ? err.message : 'Failed to load metrics');
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMetrics();
    return () => metricsAbortRef.current?.abort();
  }, [loadMetrics]);

  if (subView === 'contacts') {
    return <XeroContactsMissingPage onBack={() => { setSubView(null); loadMetrics(); }} />;
  }
  if (subView === 'invoices') {
    return <XeroInvoicesMissingPage onBack={() => { setSubView(null); loadMetrics(); }} />;
  }
  if (subView === 'outstanding') {
    return <XeroOutstandingPage onBack={() => setSubView(null)} />;
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Xero</Typography>

      {metricsError && (
        <Alert severity="error" sx={{ mb: 2 }}>{metricsError}</Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <CountCard
          label="Contacts missing Xero ID"
          value={metrics?.contacts_missing_xero_id ?? null}
          loading={metricsLoading}
          onClick={() => setSubView('contacts')}
        />
        <CountCard
          label="Invoices missing Xero ID"
          value={metrics?.invoices_missing_xero_id ?? null}
          loading={metricsLoading}
          onClick={() => setSubView('invoices')}
        />
        <CountCard
          label="Outstanding invoices"
          value={metrics?.outstanding_invoices ?? null}
          loading={metricsLoading}
          placeholder="Coming soon"
          onClick={() => setSubView('outstanding')}
        />
      </Box>

      <Tabs
        value={tabView}
        onChange={(_, v) => setTabView(v)}
        sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none', fontSize: '0.85rem' } }}
      >
        <Tab label="Bulk Operations" value="ops" />
        <Tab label="Webhook Health" value="webhook" />
      </Tabs>

      {tabView === 'ops' && <BulkOperationsPanel />}
      {tabView === 'webhook' && <WebhookHealthPanel />}
    </Box>
  );
};

// --------------------------------------------------------------------------
// Count card
// --------------------------------------------------------------------------

interface CountCardProps {
  label: string;
  value: number | null;
  loading: boolean;
  placeholder?: string;
  onClick: () => void;
}

const CountCard: React.FC<CountCardProps> = ({ label, value, loading, placeholder, onClick }) => (
  <Card sx={{ minWidth: 220, flex: '0 0 auto' }} variant="outlined">
    <CardActionArea onClick={onClick}>
      <CardContent>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
          {loading
            ? <CircularProgress size={28} />
            : value === null
              ? <Typography component="span" variant="body2" color="text.secondary">{placeholder ?? '—'}</Typography>
              : value.toLocaleString()}
        </Typography>
      </CardContent>
    </CardActionArea>
  </Card>
);

// --------------------------------------------------------------------------
// Bulk operations panel
// --------------------------------------------------------------------------

interface BulkOpResult {
  op: string;
  data: unknown;
}

const BulkOperationsPanel: React.FC = () => {
  const [running, setRunning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkOpResult | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Send monthly
  const [monthlyMaxAge, setMonthlyMaxAge] = useState(7);
  const [monthlyDryRun, setMonthlyDryRun] = useState(true);

  // Backfill contact ids
  const [bcLimit, setBcLimit] = useState(LIMIT_DEFAULT);
  const [bcRetry, setBcRetry] = useState(false);

  // Backfill contact ids by name
  const [bcnLimit, setBcnLimit] = useState(LIMIT_DEFAULT);
  const [bcnRetry, setBcnRetry] = useState(false);

  // Backfill invoice ids
  const [biLimit, setBiLimit] = useState(LIMIT_DEFAULT);
  const [biRetry, setBiRetry] = useState(false);

  // Backfill resource types
  const [brtLimit, setBrtLimit] = useState(LIMIT_DEFAULT);
  const [brtStrategy, setBrtStrategy] = useState<'probe_xero' | 'by_total_sign'>('probe_xero');

  // Mark zero invoices
  const [mzLimit, setMzLimit] = useState(LIMIT_DEFAULT);

  const runOp = async (
    op: string,
    fn: (signal: AbortSignal) => Promise<unknown>
  ) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setRunning(op);
    setError(null);
    setResult(null);
    try {
      const data = await fn(controller.signal);
      setResult({ op, data });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : `${op} failed`);
    } finally {
      setRunning(null);
    }
  };

  const limitFieldProps = {
    type: 'number' as const,
    size: 'small' as const,
    sx: { width: 120 },
    inputProps: { min: 1, max: LIMIT_MAX },
  };

  return (
    <>
      {/* Send monthly */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <SectionTitle>Send Monthly Xero Invoices</SectionTitle>
        <SectionDescription>
          Emails this month's Xero invoices to every Xero-eligible customer and marks each as sent.
          Necessary because Xero doesn't auto-deliver recurring invoices and customers expect them on a fixed cycle.
          Max age (days) bounds how far back to look for unsent invoices; Dry Run reports what would be sent without emailing.
        </SectionDescription>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            label="Max age (days)"
            type="number"
            size="small"
            value={monthlyMaxAge}
            onChange={(e) => setMonthlyMaxAge(Number(e.target.value))}
            sx={{ width: 140 }}
            inputProps={{ min: 0 }}
          />
          <FormControlLabel
            control={<Switch checked={monthlyDryRun} onChange={(e) => setMonthlyDryRun(e.target.checked)} size="small" />}
            label="Dry Run"
          />
          <RunButton
            running={running === 'monthly'}
            disabled={running !== null}
            onClick={() => runOp('monthly', (signal) =>
              runSendMonthlyXeroInvoices({ max_age_days: monthlyMaxAge, dry_run: monthlyDryRun }, signal)
            )}
          />
        </Box>
      </Paper>

      {/* Backfill contact ids */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <SectionTitle>Backfill Xero Contact IDs</SectionTitle>
        <SectionDescription>
          For Dataverse customers missing a Xero Contact ID, walks each customer's most recent
          Xero-linked invoice and copies the ContactID off it. Necessary because downstream invoice
          and payment sync requires the Contact ID, and new/migrated customers can arrive without it.
          Retry previously skipped re-attempts customers earlier marked as unresolvable.
        </SectionDescription>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            label="Limit"
            value={bcLimit}
            onChange={(e) => setBcLimit(Number(e.target.value))}
            {...limitFieldProps}
          />
          <FormControlLabel
            control={<Switch checked={bcRetry} onChange={(e) => setBcRetry(e.target.checked)} size="small" />}
            label="Retry previously skipped"
          />
          <RunButton
            running={running === 'bc'}
            disabled={running !== null || bcLimit < 1 || bcLimit > LIMIT_MAX}
            onClick={() => runOp('bc', (signal) =>
              runBackfillXeroContactIds({ all_missing: true, limit: bcLimit, retry_previously_skipped: bcRetry || undefined }, signal)
            )}
          />
          <LimitHelperCaption />
        </Box>
      </Paper>

      {/* Backfill contact ids by name */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <SectionTitle>Backfill Xero Contact IDs (by name)</SectionTitle>
        <SectionDescription>
          Resolves the Xero Contact ID by exact-matching the customer's business name (then full name)
          against Xero Contacts. Necessary for customers — typically migrated from SM8 — that have no
          Xero-linked invoices yet, so the invoice-walk flow above can't find them. Skips customers
          that already have linked invoices, ambiguous matches, or duplicates.
        </SectionDescription>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            label="Limit"
            value={bcnLimit}
            onChange={(e) => setBcnLimit(Number(e.target.value))}
            {...limitFieldProps}
          />
          <FormControlLabel
            control={<Switch checked={bcnRetry} onChange={(e) => setBcnRetry(e.target.checked)} size="small" />}
            label="Retry previously skipped"
          />
          <RunButton
            running={running === 'bcn'}
            disabled={running !== null || bcnLimit < 1 || bcnLimit > LIMIT_MAX}
            onClick={() => runOp('bcn', (signal) =>
              runBackfillXeroContactIdsByName({ all_missing: true, limit: bcnLimit, retry_previously_skipped: bcnRetry || undefined }, signal)
            )}
          />
          <LimitHelperCaption />
        </Box>
      </Paper>

      {/* Backfill invoice ids */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <SectionTitle>Backfill Xero Invoice IDs</SectionTitle>
        <SectionDescription>
          For Dataverse invoices missing a Xero Invoice ID, queries Xero by invoice number and copies
          the InvoiceID across. Necessary so each invoice can be enriched with Xero payment status,
          amount due, online URL, and history — without an InvoiceID none of those lookups work.
          Retry previously skipped re-attempts invoices earlier marked unresolvable.
        </SectionDescription>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            label="Limit"
            value={biLimit}
            onChange={(e) => setBiLimit(Number(e.target.value))}
            {...limitFieldProps}
          />
          <FormControlLabel
            control={<Switch checked={biRetry} onChange={(e) => setBiRetry(e.target.checked)} size="small" />}
            label="Retry previously skipped"
          />
          <RunButton
            running={running === 'bi'}
            disabled={running !== null || biLimit < 1 || biLimit > LIMIT_MAX}
            onClick={() => runOp('bi', (signal) =>
              runBackfillXeroInvoiceIds({ all_missing: true, limit: biLimit, retry_previously_skipped: biRetry || undefined }, signal)
            )}
          />
          <LimitHelperCaption />
        </Box>
      </Paper>

      {/* Backfill invoice resource types */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <SectionTitle>Backfill Xero Invoice Resource Types</SectionTitle>
        <SectionDescription>
          Tags each linked invoice as Invoice or CreditNote on rows that pre-date the resource-type column.
          Necessary because subsequent Xero lookups go to either /Invoices/&#123;id&#125; or /CreditNotes/&#123;id&#125; — the wrong
          path returns 404. Strategy "Probe Xero" calls Xero per row (authoritative, slow); "By total sign"
          infers from cr001_total &gt; 0 vs &lt; 0 (fast, no API calls; skips zero-total rows).
        </SectionDescription>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            label="Strategy"
            select
            size="small"
            value={brtStrategy}
            onChange={(e) => setBrtStrategy(e.target.value as 'probe_xero' | 'by_total_sign')}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="probe_xero">Probe Xero</MenuItem>
            <MenuItem value="by_total_sign">By total sign</MenuItem>
          </TextField>
          <TextField
            label="Limit"
            value={brtLimit}
            onChange={(e) => setBrtLimit(Number(e.target.value))}
            {...limitFieldProps}
          />
          <RunButton
            running={running === 'brt'}
            disabled={running !== null || brtLimit < 1 || brtLimit > LIMIT_MAX}
            onClick={() => runOp('brt', (signal) =>
              runBackfillXeroInvoiceResourceTypes({ all_missing: true, strategy: brtStrategy, limit: brtLimit }, signal)
            )}
          />
          <LimitHelperCaption />
        </Box>
      </Paper>

      {/* Mark zero invoices */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <SectionTitle>Mark Zero Invoices</SectionTitle>
        <SectionDescription>
          Stamps a sentinel Xero ID (NA_ZERO_INVOICE) on Dataverse invoices where total is 0 and no Xero
          ID is set. Pure Dataverse, no Xero calls. Necessary to remove these rows from the regular
          backfill candidate set — they were never sent to Xero and never will be, so they'd otherwise
          recur as noise on every sweep.
        </SectionDescription>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            label="Limit"
            value={mzLimit}
            onChange={(e) => setMzLimit(Number(e.target.value))}
            {...limitFieldProps}
          />
          <RunButton
            running={running === 'mz'}
            disabled={running !== null || mzLimit < 1 || mzLimit > LIMIT_MAX}
            onClick={() => runOp('mz', (signal) =>
              runMarkZeroInvoices({ all_missing: true, limit: mzLimit }, signal)
            )}
          />
          <LimitHelperCaption />
        </Box>
      </Paper>

      {running && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Running {running} — capped at {LIMIT_MAX} rows per call.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {result && <ResultPanel op={result.op} data={result.data} />}
    </>
  );
};

// --------------------------------------------------------------------------
// Webhook health panel
// --------------------------------------------------------------------------

const WebhookHealthPanel: React.FC = () => {
  const [health, setHealth] = useState<WebhookHealth | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const healthAbortRef = useRef<AbortController | null>(null);

  // Refresh form
  const [running, setRunning] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [refreshResult, setRefreshResult] = useState<unknown>(null);
  const [since, setSince] = useState('');
  const refreshAbortRef = useRef<AbortController | null>(null);

  const loadHealth = useCallback(async () => {
    healthAbortRef.current?.abort();
    const controller = new AbortController();
    healthAbortRef.current = controller;
    setHealthLoading(true);
    setHealthError(null);
    try {
      const data = await getXeroWebhookHealth(controller.signal);
      setHealth(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setHealthError(err instanceof Error ? err.message : 'Failed to load webhook health');
    } finally {
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHealth();
    return () => healthAbortRef.current?.abort();
  }, [loadHealth]);

  const handleRefresh = async () => {
    refreshAbortRef.current?.abort();
    const controller = new AbortController();
    refreshAbortRef.current = controller;
    setRunning(true);
    setRefreshError(null);
    setRefreshResult(null);
    try {
      const data = await runRefreshXeroModified(
        { since: since || undefined },
        controller.signal
      );
      setRefreshResult(data);
      loadHealth();
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setRefreshError(err instanceof Error ? err.message : 'Refresh failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <SectionTitle>Pipeline Status</SectionTitle>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            startIcon={healthLoading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
            disabled={healthLoading}
            onClick={loadHealth}
          >
            Refresh status
          </Button>
        </Box>
        {healthError && <Alert severity="error" sx={{ mb: 1 }}>{healthError}</Alert>}
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <StatusField label="Last event UTC" value={health?.last_event_utc} />
          <StatusField label="Last processed UTC" value={health?.last_processed_utc} />
          <StatusField
            label="Queue depth"
            value={health?.queue_depth === null || health?.queue_depth === undefined
              ? null
              : String(health.queue_depth)}
          />
        </Box>
        {health?.errors && health.errors.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {health.errors.join(' · ')}
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <SectionTitle>Refresh Xero Modified</SectionTitle>
        <SectionDescription>
          Manually re-runs the reconciliation backstop. Lists Xero invoices/credit notes
          modified since the cursor (or since the supplied datetime) and fans them onto
          the webhook queue. Necessary as a safety net for missed Xero webhooks.
        </SectionDescription>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            label="Since (ISO datetime, optional)"
            size="small"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            placeholder="2026-04-25T00:00:00"
            sx={{ width: 280 }}
            helperText="Empty = use the cursor in Key Vault"
          />
          <RunButton
            running={running}
            disabled={running}
            onClick={handleRefresh}
            label="Run refresh"
          />
        </Box>
      </Paper>

      {refreshError && <Alert severity="error" sx={{ mb: 2 }}>{refreshError}</Alert>}
      {refreshResult !== null && <ResultPanel op="refresh" data={refreshResult} />}
    </>
  );
};

// --------------------------------------------------------------------------
// Shared bits
// --------------------------------------------------------------------------

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="subtitle2" sx={{ mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 'bold', color: 'rgba(0,0,0,0.8)' }}>
    {children}
  </Typography>
);

const SectionDescription: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
    {children}
  </Typography>
);

const LimitHelperCaption: React.FC = () => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ alignSelf: 'center', maxWidth: 360 }}
  >
    {LIMIT_HELPER}
  </Typography>
);

interface RunButtonProps {
  running: boolean;
  disabled: boolean;
  onClick: () => void;
  label?: string;
}

const RunButton: React.FC<RunButtonProps> = ({ running, disabled, onClick, label = 'Run' }) => (
  <Button
    variant="contained"
    size="small"
    startIcon={running ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
    disabled={disabled}
    onClick={onClick}
  >
    {label}
  </Button>
);

const StatusField: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const ResultPanel: React.FC<{ op: string; data: unknown }> = ({ op, data }) => {
  if (op === 'monthly' && isSendMonthlyResponse(data)) {
    return <MonthlyResultPanel data={data} />;
  }
  return (
    <Paper sx={{ p: 2 }}>
      <SectionTitle>Result · {op}</SectionTitle>
      <Box
        component="pre"
        sx={{
          m: 0,
          fontSize: '0.75rem',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 480,
          overflow: 'auto',
        }}
      >
        {JSON.stringify(data, null, 2)}
      </Box>
    </Paper>
  );
};

function isSendMonthlyResponse(data: unknown): data is SendMonthlyXeroInvoicesResponse {
  return (
    typeof data === 'object'
    && data !== null
    && 'would_send' in data
    && 'invoices_found' in data
    && 'dry_run' in data
  );
}

// Xero serializes dates as "/Date(1735603200000+0000)/". Extract the epoch ms.
function parseXeroDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = /\/Date\((-?\d+)/.exec(value);
  if (match) {
    const ms = Number(match[1]);
    return Number.isFinite(ms) ? new Date(ms) : null;
  }
  const direct = new Date(value);
  return Number.isNaN(direct.getTime()) ? null : direct;
}

function formatXeroDate(value: string | null | undefined): string {
  const d = parseXeroDate(value);
  return d ? d.toLocaleDateString() : (value ?? '—');
}

const currencyFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
});

function formatAmount(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return currencyFormatter.format(value);
}

const MonthlyResultPanel: React.FC<{ data: SendMonthlyXeroInvoicesResponse }> = ({ data }) => {
  const headerCellSx = {
    color: 'rgba(0,0,0,0.8)',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    textTransform: 'uppercase' as const,
  };
  const bodyCellSx = { fontSize: '0.8rem' };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <SectionTitle>Result · Send Monthly Xero Invoices</SectionTitle>
        {data.dry_run && (
          <Typography
            variant="caption"
            sx={{
              ml: 1,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: 'warning.light',
              color: 'warning.contrastText',
              textTransform: 'uppercase',
              fontWeight: 'bold',
              letterSpacing: '0.06em',
            }}
          >
            Dry Run
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
        <SummaryStat label="Max age (days)" value={String(data.max_age_days)} />
        <SummaryStat label="Customers processed" value={String(data.customers_processed)} />
        <SummaryStat label="Invoices found" value={String(data.invoices_found)} />
        <SummaryStat
          label={data.dry_run ? 'Would send' : 'Sent'}
          value={String(data.dry_run ? data.would_send.length : data.sent)}
        />
        {data.failed.length > 0 && (
          <SummaryStat label="Failed" value={String(data.failed.length)} />
        )}
        {data.customers_skipped_no_xero_id.length > 0 && (
          <SummaryStat
            label="Customers skipped (no Xero ID)"
            value={String(data.customers_skipped_no_xero_id.length)}
          />
        )}
      </Box>

      {data.dry_run && data.would_send.length > 0 && (
        <Box sx={{ mb: data.failed.length > 0 ? 2 : 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Invoices that would be sent ({data.would_send.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}>
                  <TableCell sx={headerCellSx}>Invoice #</TableCell>
                  <TableCell sx={headerCellSx}>Contact</TableCell>
                  <TableCell sx={headerCellSx}>Date</TableCell>
                  <TableCell sx={headerCellSx} align="right">Amount due</TableCell>
                  <TableCell sx={headerCellSx}>Xero invoice ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.would_send.map((row, index) => (
                  <TableRow
                    key={row.invoice_id}
                    sx={{
                      backgroundColor: index % 2 === 1 ? 'action.hover' : 'transparent',
                    }}
                  >
                    <TableCell sx={bodyCellSx}>{row.invoice_number ?? '—'}</TableCell>
                    <TableCell sx={bodyCellSx}>{row.contact_name ?? '—'}</TableCell>
                    <TableCell sx={bodyCellSx}>{formatXeroDate(row.date)}</TableCell>
                    <TableCell sx={bodyCellSx} align="right">{formatAmount(row.amount_due)}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontFamily: 'monospace' }}>
                      {row.invoice_id}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {data.failed.length > 0 && (
        <Box>
          <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.5 }}>
            Failures ({data.failed.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}>
                  <TableCell sx={headerCellSx}>Xero invoice ID</TableCell>
                  <TableCell sx={headerCellSx}>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.failed.map((row, index) => (
                  <TableRow
                    key={`${row.invoice_id ?? 'unknown'}-${index}`}
                    sx={{
                      backgroundColor: index % 2 === 1 ? 'action.hover' : 'transparent',
                    }}
                  >
                    <TableCell sx={{ ...bodyCellSx, fontFamily: 'monospace' }}>
                      {row.invoice_id ?? '—'}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{row.error}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {data.dry_run && data.would_send.length === 0 && data.failed.length === 0 && (
        <Alert severity="info">No invoices match the dry-run criteria.</Alert>
      )}
    </Paper>
  );
};

const SummaryStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block' }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
);
